import { throttle } from "lodash-es"
import { computed, observable, observe, reaction, runInAction } from "mobx"
import { createRef } from "react"
import { toast } from "sonner"

import { createLLMSelection } from "@/components/LLMSelector/LLMSelection.ts"
import { CompletionModelImpl } from "@/components/PromptInput/Completion/CompletionModel.ts"
import { containsFigmaLink } from "@/components/PromptInput/Figma/FigmaUtils.ts"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { dropSecureStateCookie, FigmaSecureCookie } from "@/lib/cookieUtils.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { NoOpPersistence } from "@/lib/persistence.ts"
import { looksLikeBinary, openLinkInNewWindow, parseTaskSettings, renameFileIfNeeded } from "@/lib/std.ts"
import { validateEmail } from "@/lib/validateUtils.ts"

import type { BranchSelection } from "@/components/BranchSelection/BranchSelection.ts"
import type { ResolvedAttachment } from "@/components/Chat/Attachments.tsx"
import type { LLMSelection } from "@/components/LLMSelector/LLMSelection.ts"
import type { CompletionModel, CompletionResult } from "@/components/PromptInput/Completion/CompletionModel.ts"
import type { CompletionProvider } from "@/components/PromptInput/Completion/completionProviders.ts"
import type { RouterType } from "@/context.ts"
import type {
  AiAgentTaskContextItem,
  AiAgentTaskDomElement,
  AiAgentTaskDomElementIn,
  AiChatMessageAttachment,
  BranchInfo,
  FigmaStatus,
  InitProjectStep,
  ProjectKey,
  TID,
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Persistence } from "@/lib/persistence.ts"
import type { IObservableValue } from "mobx"
import type { ChangeEvent, DragEvent, FormEvent, KeyboardEvent, RefObject } from "react"

const log = getLogger("PromptInputModel")

export type PromptInputSubmitArgs = {
  firstInputValue: string
  secondInputValue: string
  context: AiAgentTaskContextItem[]
  attachments: Promise<AiChatMessageAttachment>[]
  elements: AiAgentTaskDomElementIn[]
  inputMode: PromptInputMode
  llmId?: string | null
}

type TaskDomElement = AiAgentTaskDomElementIn & {
  id: string
}

export type InviteDeveloperHint = {
  id: string
  title: string
  callToAction: string
}

export interface PromptInputModel {
  taskId: string | undefined

  initialValue: string
  firstInputValue: IObservableValue<string>
  secondInputValue: IObservableValue<string>

  canSubmit: IObservableValue<boolean>
  isSubmitting: IObservableValue<boolean>
  isDragging: IObservableValue<boolean>
  autoFocus: IObservableValue<boolean>
  uploadComplete: IObservableValue<boolean>
  readOnly: IObservableValue<boolean>
  liveUpdateDebugEvents: IObservableValue<boolean>

  context: IObservableValue<AiAgentTaskContextItem[]>
  attachments: IObservableValue<Promise<AiChatMessageAttachment>[]>
  resolvedAttachments: IObservableValue<ResolvedAttachment[]>
  elements: IObservableValue<TaskDomElement[]>

  completionModel: CompletionModel
  validationError: IObservableValue<string | null>
  supportedInputModes: IObservableValue<PromptInputMode[]>
  selectedInputMode: IObservableValue<PromptInputMode>
  previousInputMode: PromptInputMode | null
  llmSelection: IObservableValue<LLMSelection | null>
  branchSelection: IObservableValue<BranchSelection | null>

  initProjectStep: IObservableValue<InitProjectStep | null>
  inviteDeveloperHint: IObservableValue<InviteDeveloperHint | null>

  persistence?: Persistence

  containerRef: RefObject<HTMLFormElement | null>
  inputRef: RefObject<HTMLTextAreaElement | null>
  tokenInputRef: RefObject<HTMLInputElement | null>

  removeDomAttachment: (attachment: TaskDomElement) => void

  onSubmit: (event: FormEvent) => Promise<void>
  doSubmit: () => Promise<void>
  submitPrompt: (prompt: string) => Promise<void>
  onSelect: (event: ChangeEvent<HTMLTextAreaElement>, index: number) => void
  onInputChanged: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, index: number) => void
  onKeyDown: (event: KeyboardEvent) => void
  onFocus: () => void
  onBlur: () => void

  onUploadAttachment: (file: File) => Promise<AiChatMessageAttachment> | null
  downloadAttachment: (file: AiChatMessageAttachment) => void

  addMention: () => void
  highlight: () => void

  handlePaste: (e: Event, clipboardData: DataTransfer | null) => void
  handleDrop: (e: DragEvent<HTMLFormElement>) => void
  handleDragOver: (e: DragEvent<HTMLFormElement>) => void
  handleDragLeave: () => void

  setInputMode: (mode: PromptInputModeType) => void
  setSelectedBranch: (branch: BranchInfo | null) => void

  figmaStatus: IObservableValue<FigmaStatus | null>
  showFigmaWarning: IObservableValue<boolean>
  connectToFigma: () => Promise<void>

  installRemoteMCP: (name: string, url: string) => void
}

export type PromptInputInitialState = {
  prompt: string
  context: AiAgentTaskContextItem[]
  attachments: AiChatMessageAttachment[]
  domElements: AiAgentTaskDomElement[]
}

type PromptInputModelProps = {
  name: string
  submit: (args: PromptInputSubmitArgs) => Promise<boolean | void>

  router?: RouterType
  resetOnNavigation?: boolean

  persistence?: Persistence
  orgContext?: OrgContext

  validate?: (args: PromptInputSubmitArgs) => Promise<string | null>

  taskId?: string
  completionProvider: CompletionProvider
  completionBatchSize?: number

  autoFocus?: IObservableValue<boolean>
  persistable?: IObservableValue<boolean>

  uploadAttachment?: (file: File) => Promise<AiChatMessageAttachment>

  initialState?: PromptInputInitialState

  // Flags set by the onboarding flow
  initProjectStep?: IObservableValue<InitProjectStep | null>
  inviteDeveloperHint?: IObservableValue<InviteDeveloperHint | null>
  canInviteDeveloper?: IObservableValue<boolean>
  canRequestSupport?: IObservableValue<boolean>

  readOnly?: IObservableValue<boolean>
  liveUpdateDebugEvents?: IObservableValue<boolean>
}

export type CommitContext = {
  projectKey: ProjectKey
  repo: string
  commit: string | null
}

const FIRST_INPUT_STORAGE_KEY_BASE = "first-input"
const SECOND_INPUT_STORAGE_KEY_BASE = "second-input"

const getStorageKeyForMode = (baseKey: string, mode: PromptInputMode): string => {
  return `${baseKey}-${mode.type}`
}

export type PromptInputModeType =
  "Url"
  | "Token"
  | "SshKey"
  | "BasicAuth"
  | "ProjectName"
  | "Chat"
  | "InviteDeveloper"
  | "RequestSupport"
  | "None"

type PromptInputExtensions = {
  attachments: boolean
  llmSelector: boolean
  branchSelector: boolean
  guidelines: boolean
  mentions: boolean
  figma: boolean
  mcp: boolean
}

export type PromptInputMode = {
  type: PromptInputModeType
  name: string
  hasSecondInput: boolean
  firstInputPlaceholder: string
  secondInputPlaceholder: string
  isFirstInputSecret: boolean
  isSecondInputSecret: boolean
  persistable: boolean
  extensions: PromptInputExtensions
  canSubmit: (args: PromptInputSubmitArgs) => boolean
  setPreviousModeOnSubmit: boolean
}

export class PromptInputModelImpl extends Model implements PromptInputModel {
  readonly taskId: string | undefined

  readonly initialValue: string
  readonly firstInputValue = observable.box("")
  readonly secondInputValue = observable.box("")

  readonly isSubmitting = observable.box(false)
  readonly isDragging = observable.box(false)
  readonly autoFocus = observable.box(false)
  readonly readOnly = observable.box(false)

  readonly context = observable.box<AiAgentTaskContextItem[]>([])
  readonly attachments = observable.box<Promise<AiChatMessageAttachment>[]>([])
  readonly resolvedAttachments = observable.box<ResolvedAttachment[]>([])
  readonly elements = observable.box<TaskDomElement[]>([])

  private readonly attachmentBlobs = new Map<Promise<AiChatMessageAttachment>, Blob>()

  readonly uploadComplete = computed(() => {
    return this.resolvedAttachments.get().every(it => it.status === "resolved" || it.status === "failed")
  })

  private readonly selectedInputModeIndex = observable.box<number>(0)
  readonly validationError: IObservableValue<string | null> = observable.box(null)
  readonly llmSelection = observable.box<LLMSelection | null>(null)
  readonly branchSelection = observable.box<BranchSelection | null>(null)

  readonly initProjectStep: IObservableValue<InitProjectStep | null> = observable.box(null)
  readonly inviteDeveloperHint = observable.box<InviteDeveloperHint | null>(null)

  private readonly router?: RouterType
  private readonly resetOnNavigation?: boolean

  persistence?: Persistence
  private readonly orgContext?: OrgContext

  readonly completionModel: CompletionModel

  readonly containerRef = createRef<HTMLFormElement>()
  readonly inputRef = createRef<HTMLTextAreaElement>()
  readonly tokenInputRef = createRef<HTMLInputElement>()

  readonly liveUpdateDebugEvents = observable.box(false)
  private readonly canInviteDeveloper = observable.box(false)
  private readonly canRequestSupport = observable.box(false)

  readonly figmaStatus = observable.box<FigmaStatus | null>(null)
  readonly showFigmaWarning = observable.box(false)
  private figmaConnectionUrl: string | null = null

  private readonly submit: (
    args: PromptInputSubmitArgs
  ) => Promise<boolean | void>

  private readonly validate: (
    args: PromptInputSubmitArgs
  ) => Promise<string | null>

  private readonly upload?: (file: File) => Promise<AiChatMessageAttachment>
  readonly downloadAttachment: (file: AiChatMessageAttachment) => void

  readonly supportedInputModes = computed(() => {
    const step = this.initProjectStep.get()
    if (this.readOnly.get()) {
      return []
    }
    let modes: PromptInputMode[]
    if (step === "AddSSHKey") {
      modes = [sshKeyInputMode, askingInputMode]
    } else if (step === "EnterBasicAuth") {
      modes = [basicAuthInputMode, askingInputMode]
    } else if (step === "EnterProjectName") {
      modes = [projectNameInputMode]
    } else if (step === "EnterToken") {
      modes = [tokenInputMode, askingInputMode]
    } else if (step === "EnterUrl") {
      modes = [urlInputMode]
    } else if (step === "WaitForMirroring") {
      modes = []
    } else if (this.taskId === undefined) {
      modes = [newTaskInputMode]
    } else {
      modes = [this.canInviteDeveloper.get() ? onboardingInputMode : codingInputMode]
    }

    if (modes.length > 0 && this.canInviteDeveloper.get()) {
      modes.push(inviteDeveloperInputMode)
    }

    if (modes.length > 0 && this.canRequestSupport.get()) {
      modes.push(requestSupportInputMode)
    }

    return modes
  })

  readonly selectedInputMode: IObservableValue<PromptInputMode> = computed(() => {
    const inputModeIndex = this.selectedInputModeIndex.get()
    const availableInputModes = this.supportedInputModes.get()
    const step = this.initProjectStep.get()
    if (availableInputModes.length === 0) {
      if (step === "WaitForMirroring" || step === "CreatingRepository") {
        return mirroringInputMode
      } else {
        return disabledInputMode
      }
    }
    return inputModeIndex < availableInputModes.length ? availableInputModes[inputModeIndex] : availableInputModes[0]
  })

  previousInputMode: PromptInputMode | null = null
  private savedSecretValues = new Map<PromptInputModeType, { first: string, second: string }>()

  readonly setInputMode = (type: PromptInputModeType) => {
    runInAction(() => {
      const inputModeIndex = this.supportedInputModes.get().map(it => it.type).indexOf(type)
      if (inputModeIndex >= 0 && inputModeIndex < this.supportedInputModes.get().length) {
        this.previousInputMode = this.selectedInputMode.get()
        this.selectedInputModeIndex.set(inputModeIndex)
      }
    })
  }

  readonly setSelectedBranch = (branch: BranchInfo | null) => {
    runInAction(() => {
      this.branchSelection.get()?.selectedBaseBranch.set(branch)
    })
  }

  constructor(props: PromptInputModelProps) {
    super(props.name)

    this.taskId = props.taskId

    this.router = props.router
    this.resetOnNavigation = props.resetOnNavigation

    if (props.persistable) {
      this.lifetime.add(reaction(
        () => props.persistable?.get(),
        (isPersistable) => {
          if (isPersistable) {
            this.persistence = props.persistence ?? (props.taskId && props.orgContext?.persistence
              ? props.orgContext.persistence.subStorage(`project/${props.taskId}`)
              : undefined)
          } else {
            this.persistence = undefined
          }
        },
        { fireImmediately: true }
      ), "PromptInputModelImpl:persistable")
    } else {
      this.persistence = undefined
    }

    this.orgContext = props.orgContext

    this.completionModel = new CompletionModelImpl(this.lifetime, props.completionProvider, () => this.firstInputValue.get(), this.onCompleted, props.completionBatchSize)

    if (props.autoFocus) {
      this.autoFocus = props.autoFocus
    }

    if (props.liveUpdateDebugEvents) {
      this.liveUpdateDebugEvents = props.liveUpdateDebugEvents
    }

    this.submit = props.submit

    this.validate = props.validate ?? (async () => null)

    const queries = props.orgContext?.queries
    const taskId = props.taskId
    this.upload = queries ? (file => queries.apiClient.uploadFile(file)) : props.uploadAttachment
    this.downloadAttachment = (queries && taskId) ? (file => queries.apiClient.downloadFile(taskId as TID, file.id)) : (_ => {
    })

    this.initProjectStep = props.initProjectStep ?? observable.box(null)
    this.inviteDeveloperHint = props.inviteDeveloperHint ?? observable.box(null)

    if (props.readOnly) {
      this.readOnly = props.readOnly
    }

    if (props.canInviteDeveloper) {
      this.canInviteDeveloper = props.canInviteDeveloper
    }

    if (props.canRequestSupport) {
      this.canRequestSupport = props.canRequestSupport
    }

    void this.initLlmSelection()

    this.initialValue = props.initialState?.prompt ?? ""

    this.lifetime.add(reaction(
      () => ({
        figmaConnected: this.figmaStatus.get(),
      }),
      ({ figmaConnected }) => {
        if (figmaConnected?.isConnected === false) {
          runInAction(() => void this.getFigmaConnectUrl())
        }
        if (figmaConnected?.isConnected === true) {
          dropSecureStateCookie(FigmaSecureCookie)
        }
      }
    ), "PromptInputModelImpl:figmaStatus")

    void this.subscribeToFigmaAuthStatus()

    this.lifetime.add(reaction(
      () => ({
        firstInput: this.firstInputValue.get(),
        secondInput: this.secondInputValue.get(),
      }),
      ({ firstInput, secondInput }) => {
        const mode = this.selectedInputMode.get()
        runInAction(() => this.validationError.set(null))
        this.maybeSaveInputValuesToPersistence(mode, firstInput, secondInput)
        if (firstInput.length > 0 || secondInput.length > 0) {
          void this.notifyTyping()
        } else {
          void this.notifyTypingStopped()
        }
      }
    ), "PromptInputModelImpl:prompt")

    this.lifetime.add(observe(this.selectedInputMode, (change) => {
      if (change.oldValue === undefined && change.newValue) {
        runInAction(() => {
          if (props.initialState) {
            this.firstInputValue.set(props.initialState.prompt)
            this.context.set(props.initialState.context)
            this.attachments.set(props.initialState.attachments.map(it => Promise.resolve(it)))
          } else {
            this.loadModeFromPersistenceOrReset(change.newValue)
          }
        })
      }

      if (change.oldValue && change.newValue.type !== change.oldValue.type) {
        runInAction(() => this.validationError.set(null))
        this.loadModeFromPersistenceOrReset(change.newValue)
      }
    }, true), "PromptInputModelImpl:reset")

    this.lifetime.add(reaction(
      () => ({
        pendingAttachments: this.attachments.get().map(promise => ({
          status: "pending" as const,
          promise,
          blob: this.attachmentBlobs.get(promise),
        } as ResolvedAttachment))
      }),
      ({ pendingAttachments }) => {
        runInAction(() => this.resolvedAttachments.set(pendingAttachments))

        pendingAttachments.forEach((entry, index) => {
          void entry.promise.then(result => {
            runInAction(() => {
              const prev = this.resolvedAttachments.get()
              const next = [...prev]
              next[index] = { ...entry, status: "resolved", result }
              this.resolvedAttachments.set(next)
            })
          }).catch((e) => {
            runInAction(() => {
              const prev = this.resolvedAttachments.get()
              const next = [...prev]
              next[index] = { ...entry, status: "failed", error: e instanceof Error ? e : new Error(String(e)) }
              this.resolvedAttachments.set(next)
            })
          })
        })
      }
    ), "PromptInputModelImpl:pendingAttachments")

    // When submitting, we need to clear the input values ASAP if the input mode changes to avoid flashing the secret value
    this.lifetime.add(reaction(
      () => ({
        isSubmitting: this.isSubmitting.get(),
        selectedInputMode: this.selectedInputMode.get(),
      }),
      ({ isSubmitting, selectedInputMode }, prev) => {
        if (isSubmitting) {
          if (selectedInputMode.isFirstInputSecret != prev.selectedInputMode.isFirstInputSecret) {
            runInAction(() => this.firstInputValue.set(""))
          }

          if (selectedInputMode.isSecondInputSecret != prev.selectedInputMode.isSecondInputSecret) {
            runInAction(() => this.secondInputValue.set(""))
          }
        }
      }
    ), "PromptInputModelImpl:selectedInputMode")
  }

  private readonly loadModeFromPersistenceOrReset = (mode: PromptInputMode) => {
    // Save current secret values before resetting
    const currentMode = this.previousInputMode
    if (currentMode && (currentMode.isFirstInputSecret || currentMode.isSecondInputSecret)) {
      runInAction(() => {
        this.savedSecretValues.set(currentMode.type, {
          first: this.firstInputValue.get(),
          second: this.secondInputValue.get()
        })
      })
    }

    this.reset()

    // Check if we have saved secret values for this mode
    const savedSecrets = this.savedSecretValues.get(mode.type)
    if (savedSecrets) {
      runInAction(() => {
        if (mode.isFirstInputSecret && savedSecrets.first) {
          this.firstInputValue.set(savedSecrets.first)
        }
        if (mode.isSecondInputSecret && savedSecrets.second) {
          this.secondInputValue.set(savedSecrets.second)
        }
      })
    }

    if (!mode.persistable) {
      return
    }

    const firstInputKey = getStorageKeyForMode(FIRST_INPUT_STORAGE_KEY_BASE, mode)
    const secondInputKey = getStorageKeyForMode(SECOND_INPUT_STORAGE_KEY_BASE, mode)

    const savedFirstInput = this.persistence?.getItem(firstInputKey)
    const savedSecondInput = this.persistence?.getItem(secondInputKey)

    runInAction(() => {
      if (savedFirstInput && !mode.isFirstInputSecret) {
        this.firstInputValue.set(savedFirstInput)
        this.showFigmaWarning.set(containsFigmaLink(savedFirstInput) && this.figmaStatus.get()?.isConnected == false)
      }

      if (savedSecondInput && mode.hasSecondInput && !mode.isSecondInputSecret) {
        this.secondInputValue.set(savedSecondInput)
      }
    })
  }

  private readonly maybeSaveInputValuesToPersistence = (mode: PromptInputMode, firstInput: string, secondInput: string) => {
    if (!mode.persistable) return

    if (!mode.isFirstInputSecret) {
      const firstInputKey = getStorageKeyForMode(FIRST_INPUT_STORAGE_KEY_BASE, mode)
      this.persistence?.setItem(firstInputKey, firstInput)
    }

    if (mode.hasSecondInput && !mode.isSecondInputSecret) {
      const secondInputKey = getStorageKeyForMode(SECOND_INPUT_STORAGE_KEY_BASE, mode)
      this.persistence?.setItem(secondInputKey, secondInput)
    }
  }

  private readonly notifyTyping = throttle(async () => {
    if (this.taskId && this.orgContext) {
      await this.orgContext.queries.apiClient.sendTypingEvent(this.taskId, false)
    }
  }, 500)

  private readonly notifyTypingStopped = async () => {
    if (this.taskId && this.orgContext) {
      await this.orgContext.queries.apiClient.sendTypingEvent(this.taskId, true)
    }
  }

  private readonly initLlmSelection = async () => {
    if (!this.orgContext || !this.orgContext.queries) return

    try {
      const selectionPersistence = this.taskId ? new NoOpPersistence() : this.orgContext.persistence
      const selection = await createLLMSelection(this.orgContext.queries, selectionPersistence)
      if (selection) {
        runInAction(() => this.llmSelection.set(selection))

        if (this.taskId) {
          const taskDetails = await this.orgContext.queries.taskDetails(this.taskId)
          const settings = parseTaskSettings(taskDetails.task)
          const llmId = settings.llmProfile
          if (llmId) {
            runInAction(() => selection.selectedLlmId.set(llmId))
          }
        }
      }
    } catch (error) {
      log.error("Failed to initialize LLM selection:", error)
    }
  }

  canSubmit = computed(() => {
    const inputMode = this.selectedInputMode.get()
    return inputMode.canSubmit({
      firstInputValue: this.firstInputValue.get().trim(),
      secondInputValue: this.secondInputValue.get().trim(),
      context: this.context.get(),
      attachments: this.attachments.get(),
      elements: this.elements.get(),
      inputMode
    })
  })

  readonly removeDomAttachment = (attachment: TaskDomElement) => {
    runInAction(() => {
      const filtered = this.elements.get().filter(it => {
        return it.id !== attachment.id
      })

      this.elements.set(filtered)
    })
  }

  readonly onCompleted = (result: CompletionResult) => {
    const input = this.inputRef.current
    input && runInAction(() => {
      this.firstInputValue.set(result.text)

      this.onTextRendered(input, result.text, 0, () => {
        input.setSelectionRange(result.caretOffset, result.caretOffset)
        this.completionModel.onChange(input.value, input.selectionStart)
      })
    })
  }

  readonly onSubmit = (event: FormEvent) => {
    event.preventDefault()

    return this.doSubmit()
  }

  readonly doSubmit = async () => {
    if (this.readOnly.get() || this.isSubmitting.get()) {
      return
    }

    runInAction(() => this.isSubmitting.set(true))

    const attachments = this.attachments.get()
    const validAttachments: Promise<AiChatMessageAttachment>[] = []

    for (const attachment of attachments) {
      try {
        validAttachments.push(Promise.resolve(await attachment))
      } catch {
        log.debug("Skipping invalid attachment", attachment)
      }
    }

    const submitArgs: PromptInputSubmitArgs = {
      firstInputValue: this.firstInputValue.get().trim(),
      secondInputValue: this.secondInputValue.get().trim(),
      context: this.context.get(),
      attachments: validAttachments,
      elements: this.elements.get(),
      inputMode: this.selectedInputMode.get(),
      llmId: this.llmSelection.get()?.selectedLlmId.get() ?? null
    }

    const validationError = await this.validate(submitArgs)

    if (validationError) {
      runInAction(() => {
        this.validationError.set(validationError)
        this.isSubmitting.set(false)
      })
      return
    }

    try {
      const success = await this.submit(submitArgs)
      if (success === false) {
        return
      }

      if (this.router && this.resetOnNavigation === true) {
        // Postpone resetting, we don't want a flash of the empty form
        this.lifetime.add(this.router.subscribe("onBeforeLoad", () => {
          this.reset()
        }), "PromptInputModelImpl:router")
      } else {
        this.reset()
      }

      const previousMode = this.previousInputMode
      if (this.selectedInputMode.get().setPreviousModeOnSubmit && previousMode) {
        this.setInputMode(previousMode.type)
      }
    } finally {
      runInAction(() => {
        this.isSubmitting.set(false)
        setTimeout(() => this.inputRef.current?.focus(), 100)
      })
    }
  }

  readonly submitPrompt = async (prompt: string) => {
    runInAction(() => this.isSubmitting.set(true))
    try {
      await this.submit({
        firstInputValue: prompt.trim(),
        secondInputValue: "",
        context: [],
        attachments: [],
        elements: [],
        inputMode: codingInputMode,
        llmId: this.llmSelection.get()?.selectedLlmId.get() ?? null
      })
      this.reset()
    } finally {
      runInAction(() => {
        this.isSubmitting.set(false)
        setTimeout(() => this.inputRef.current?.focus(), 100)
      })
    }
  }

  private reset() {
    runInAction(() => {
      this.firstInputValue.set("")
      this.secondInputValue.set("")
      this.context.set([])
      this.attachments.set([])
      this.elements.set([])
    })
  }

  readonly onFocus = () => {
    this.completionModel.onInputFocus()
  }

  readonly onBlur = () => {
    this.completionModel.onInputBlur()
  }

  readonly onInputChanged = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, index: number) => {
    runInAction(() => {
      const value = index === 0 ? this.firstInputValue : this.secondInputValue
      value.set(event.target.value)
      if (this.selectedInputMode.get().extensions.mentions) {
        const selectionStart = event.target.selectionStart
        if (selectionStart != null) {
          this.completionModel.onChange(event.target.value, selectionStart)
        }
      }
      this.showFigmaWarning.set(containsFigmaLink(value.get()) && this.figmaStatus.get()?.isConnected == false)
    })
  }

  readonly onSelect = (event: ChangeEvent<HTMLTextAreaElement>, index: number) => {
    runInAction(() => {
      const value = index === 0 ? this.firstInputValue : this.secondInputValue
      value.set(event.target.value)
      if (this.selectedInputMode.get().extensions.mentions) {
        const selectionStart = event.target.selectionStart
        if (selectionStart != null) {
          this.completionModel.onChange(event.target.value, selectionStart)
        }
      }
    })
  }

  readonly onKeyDown = (event: KeyboardEvent) => {
    const complete = () => {
      const items = this.completionModel.items.get()
      const selectionIndex = this.completionModel.selectionIndex.get()
      if (selectionIndex >= 0 && selectionIndex < items.length) {
        this.completionModel.complete(items[selectionIndex])
      }
    }

    if (this.completionModel.isVisible.get()) {
      if (event.key === "Enter") {
        event.preventDefault()
        complete()
      } else if (event.key === "Escape") {
        event.preventDefault()
        this.completionModel.close()
      } else if (event.key === "ArrowDown") {
        event.preventDefault()
        this.completionModel.down()
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        this.completionModel.up()
      } else if (event.key === "Tab") {
        event.preventDefault()
        complete()
      }
    }
  }

  readonly onUploadAttachment = (file: File) => {
    if (this.selectedInputMode.get().type === "SshKey") {
      void this.onSshKeyUploaded(file)
      return null
    }
    if (!this.upload) {
      toast.error("Attachment upload is not supported")
      return null
    }

    const uploadPromise = this.upload(file)
    this.attachmentBlobs.set(uploadPromise, file)
    return uploadPromise
  }

  readonly addMention = () => {
    runInAction(() => {
      const currentInputValue = this.firstInputValue.get()
      const firstInputValue = currentInputValue.trim().length > 0 ? `${currentInputValue} @` : "@"
      this.firstInputValue.set(firstInputValue)
      this.completionModel.onChange(firstInputValue, firstInputValue.length)
    })
  }

  readonly highlight = () => {
    this.containerRef.current?.classList.add(PromptInputStyles.Highlighted)

    setTimeout(() => {
      this.containerRef.current?.classList.remove(PromptInputStyles.Highlighted)
    }, 1000)
  }

  readonly handleDragOver = (e: DragEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!this.isSubmitting.get() && this.selectedInputMode.get().extensions.attachments && e.dataTransfer.types.includes("Files")) {
      runInAction(() => this.isDragging.set(true))
    }
  }

  readonly handleDragLeave = () => {
    runInAction(() => this.isDragging.set(false))
  }

  readonly handleDrop = (e: DragEvent<HTMLFormElement>) => {
    e.preventDefault()

    runInAction(() => this.isDragging.set(false))

    if (this.isSubmitting.get() || !this.selectedInputMode.get().extensions.attachments) return

    const files = e.dataTransfer.files
    if (files.length > 0) {
      void Promise.all(this.attachments.get()).then(attachments => {
        const filenames = attachments.map(it => it.filename)

        for (const file of files) {
          const renamedFile = renameFileIfNeeded(file, filenames)
          const uploadPromise = this.onUploadAttachment(renamedFile)
          if (uploadPromise !== null) {
            runInAction(() => this.attachments.set([...this.attachments.get(), uploadPromise]))
          }
        }
      })
      this.highlight()
    }
  }

  readonly handlePaste = (e: Event, clipboardData: DataTransfer | null) => {
    // Handle attachments
    if (!this.selectedInputMode.get().extensions.attachments) return

    const items = clipboardData?.items
    if (!items) return

    void Promise.all(this.attachments.get()).then(attachments => {
      const filenames = attachments.map(it => it.filename)

      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile()

          if (file) {
            e.preventDefault()
            const renamedFile = renameFileIfNeeded(file, filenames)
            const uploadPromise = this.onUploadAttachment(renamedFile)
            if (uploadPromise !== null) {
              runInAction(() => this.attachments.set([...this.attachments.get(), uploadPromise]))
              this.highlight()
            }
          }
        }
      }
    })
  }

  private onTextRendered(element: HTMLTextAreaElement, text: string, frameNumber: number, callback: () => void) {
    if (element.value === text) {
      return callback()
    }

    if (frameNumber < 10) {
      requestAnimationFrame(() => {
        this.onTextRendered(element, text, frameNumber + 1, callback)
      })
    }
  }

  static async readSshPrivateKeyFile(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const value = e.target?.result
        if (typeof value === "string" && !looksLikeBinary(value)) {
          resolve(value)
        } else {
          toast.error("Failed to read the private key file")
          reject(new Error("Invalid private key file"))
        }
      }
      reader.onerror = () => {
        toast.error("Failed to read the private key file")
        reject(new Error("File read error"))
      }
      reader.readAsText(file)
    })
  }

  readonly onSshKeyUploaded = async (file: File) => {
    try {
      const value = await PromptInputModelImpl.readSshPrivateKeyFile(file)
      runInAction(() => this.firstInputValue.set(value))
      this.highlight()
    } catch {
      // errors/toasts are handled in the helper
    }
  }

  readonly installRemoteMCP = async (name?: string, url?: string) => {
    const context = this.orgContext
    if (context == null) return
    const queries = context.queries
    if (queries == null) return

    if (name == null) return
    if (url == null) return

    const resultUrl = await withGlobalLoader(context, queries.apiClient.installRemoteMCP(name, url))
    openLinkInNewWindow(resultUrl)
  }

  readonly connectToFigma = async () => {
    if (!this.orgContext) return
    if (this.figmaConnectionUrl != null) return openLinkInNewWindow(this.figmaConnectionUrl)

    const url = await withGlobalLoader(this.orgContext, this.getFigmaConnectUrl())
    url && openLinkInNewWindow(url)
  }

  private async subscribeToFigmaAuthStatus() {
    if (!this.orgContext) return
    try {
      const subscription = await withGlobalLoader(this.orgContext, this.orgContext.queries.subscribeToFigmaAuthStatus())
      runInAction(() => this.figmaStatus.set(subscription.initial))

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.figmaStatus.set(status))
      }), "PromptInputModel:subscribeToFigmaAuthStatus")
    } catch (e) {
      log.error("Failed to subscribe to Figma status:", e)
    }
  }

  private async getFigmaConnectUrl() {
    if (!this.orgContext || !this.orgContext.queries) return
    if (this.figmaConnectionUrl != null) {
      return this.figmaConnectionUrl
    }
    try {
      const url = await this.orgContext.queries.apiClient.startFigmaOAuthFlow()
      if (!url) return

      this.figmaConnectionUrl = url
      return url
    } catch {
      return null
    }
  }
}

const disabledInputMode: PromptInputMode = {
  type: "None",
  name: "Disabled",
  secondInputPlaceholder: "",
  firstInputPlaceholder: "Task is read-only, sending messages is disabled",
  hasSecondInput: false,
  extensions: {
    attachments: false,
    llmSelector: false,
    branchSelector: false,
    guidelines: false,
    mentions: false,
    figma: false,
    mcp: false,
  },
  isFirstInputSecret: false,
  isSecondInputSecret: false,
  persistable: false,
  canSubmit: () => false,
  setPreviousModeOnSubmit: false,
}

const mirroringInputMode: PromptInputMode = {
  ...disabledInputMode,
  firstInputPlaceholder: "Messaging is disabled during mirroring"
}

const codingInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "Chat",
  name: "Chat",
  firstInputPlaceholder: "Add instructions",
  extensions: {
    attachments: true,
    llmSelector: true,
    branchSelector: false,
    guidelines: true,
    mentions: true,
    figma: true,
    mcp: true,
  },
  persistable: true,
  canSubmit: (args) => {
    return args.firstInputValue.trim().length > 0 ||
      args.attachments.length > 0 ||
      args.elements.length > 0 ||
      args.context.length > 0
  }
}

const onboardingInputMode: PromptInputMode = {
  ...codingInputMode,
  extensions: {
    ...codingInputMode.extensions,
    branchSelector: false,
    guidelines: false,
    figma: false,
    mcp: false,
  }
}

const newTaskInputMode: PromptInputMode = {
  ...codingInputMode,
  extensions: {
    ...codingInputMode.extensions,
    branchSelector: true,
  }
}

const askingInputMode: PromptInputMode = {
  ...onboardingInputMode,
  firstInputPlaceholder: "Ask anything",
}

const tokenInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "Token",
  name: "Token",
  firstInputPlaceholder: "Enter personal token",
  isFirstInputSecret: true,
  persistable: true,
  canSubmit: (args) => args.firstInputValue.trim().length > 0
}

const sshKeyInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "SshKey",
  name: "SSH key",
  firstInputPlaceholder: "Enter private SSH key or drag'n'drop a file here",
  secondInputPlaceholder: "Passphrase (optional)",
  hasSecondInput: true,
  extensions: {
    ...disabledInputMode.extensions,
    attachments: true
  },
  isFirstInputSecret: true,
  isSecondInputSecret: true,
  persistable: true,
  canSubmit: (args) => args.firstInputValue.trim().length > 0
}

const projectNameInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "ProjectName",
  name: "Project name",
  persistable: true,
  firstInputPlaceholder: "Enter project name",
  canSubmit: (args) => args.firstInputValue.trim().length > 0
}

const urlInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "Url",
  name: "Repository URL",
  firstInputPlaceholder: "Enter Git repository URL",
  canSubmit: (args) => args.firstInputValue.trim().length > 0
}

const basicAuthInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "BasicAuth",
  name: "Username & password",
  firstInputPlaceholder: "Enter username",
  secondInputPlaceholder: "Enter password",
  hasSecondInput: true,
  isSecondInputSecret: true,
  canSubmit: (args) => args.firstInputValue.trim().length > 0 && args.secondInputValue.length > 0
}

const inviteDeveloperInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "InviteDeveloper",
  name: "Invite a developer",
  firstInputPlaceholder: "Enter email",
  secondInputPlaceholder: "Describe your problem (optional)",
  hasSecondInput: true,
  canSubmit: (args) => {
    const email = args.firstInputValue.trim()
    return email.length > 0 && validateEmail(email)
  },
  setPreviousModeOnSubmit: true,
}

const requestSupportInputMode: PromptInputMode = {
  ...disabledInputMode,
  type: "RequestSupport",
  name: "Request support",
  firstInputPlaceholder: "Describe your problem",
  hasSecondInput: false,
  canSubmit: (args) => args.firstInputValue.trim().length > 0,
  setPreviousModeOnSubmit: true,
}
